/**
 * ArticlesListController — page /catalogue.html (EPIC-010 / 10.3).
 *
 * Hydrate la liste paginée du catalogue d'articles depuis `GET /articles`
 * et permet : recherche (debounce 300 ms), filtre par activité (drop-down
 * peuplée dynamiquement via `GET /activities`), filtre statut (actif /
 * archivé / tous), tri sur `label` / `priceExclVat` / `activity`,
 * pagination.
 *
 * Pas de mutations depuis cette page — l'archivage et la restauration sont
 * sur la page détail (EPIC-010 / 10.4).
 *
 * Le filtre « Type » de la maquette (Service / Produit) est remappé en
 * filtre « Activité » (décision 10.3) : l'EPIC-010 ne définit pas de type
 * sur Article, le filtre métier réel est `filter[activityId]`.
 *
 * Convention CLAUDE.md respectée : pas d'HTML inline en JS (clone du
 * `<template>` + remplissage data-slot + setAttribute). Aucune classe CSS
 * inventée — toutes les classes utilisées sont dans notamicro.css (copie
 * bit-à-bit de maquettes/shared/styles.css).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const CATEGORY_LABELS = {
  bic_vente: "BIC vente",
  bic_services: "BIC services",
  bnc: "BNC",
};

const CATEGORY_PILL_CLASSES = {
  bic_vente: "nm-pill--indigo",
  bic_services: "nm-pill--blue",
  bnc: "nm-pill--olive",
};

const PRICE_FORMATTER = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export default class ArticlesListController extends BaseController {
  static targets = [
    "tableWrap",
    "tableBody",
    "summary",
    "searchInput",
    "activityFilter",
    "statusFilter",
    "pagination",
    "pageInfo",
    "pageButtons",
  ];

  static values = {
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 25 },
    q: { type: String, default: "" },
    sort: { type: String, default: "label" },
    statusFilter: { type: String, default: "active" },
    activityFilter: { type: String, default: "" },
  };

  connect() {
    this._debounceTimer = null;
    this._fetchSeq = 0;
    this._activitiesCache = new Map();
    this._populateActivityFilter().then(() => this.refresh());
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  // ---------------------------------------------------------------------------
  // Filter / search / sort / paginate actions
  // ---------------------------------------------------------------------------

  search(event) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    const value = event.target.value;
    this._debounceTimer = setTimeout(() => {
      this.qValue = value;
      this.pageValue = 1;
      this.refresh();
    }, 300);
  }

  changeActivity(event) {
    this.activityFilterValue = event.target.value;
    this.pageValue = 1;
    this.refresh();
  }

  changeStatus(event) {
    this.statusFilterValue = event.target.value;
    this.pageValue = 1;
    this.refresh();
  }

  sort(event) {
    const button = event.currentTarget;
    const field = button.dataset.sortField;
    if (!field) return;
    const current = button.getAttribute("aria-sort");
    const nextDirection = current === "ascending" ? "descending" : "ascending";
    this._updateSortHeaders(field, nextDirection);
    this.sortValue = nextDirection === "descending" ? `-${field}` : field;
    this.pageValue = 1;
    this.refresh();
  }

  paginate(event) {
    const page = Number(event.currentTarget.dataset.page);
    if (!Number.isFinite(page) || page < 1) return;
    this.pageValue = page;
    this.refresh();
  }

  reset() {
    // L'événement `reset` est dispatché AVANT que le navigateur ne restaure
    // les valeurs par défaut des champs — on pose donc explicitement les
    // valeurs par défaut (values stockées + champs DOM) puis on refresh,
    // sans dépendre du timing du reset natif.
    this.activityFilterValue = "";
    this.statusFilterValue = "active";
    this.qValue = "";
    this.sortValue = "label";
    this.pageValue = 1;
    if (this.hasActivityFilterTarget) this.activityFilterTarget.value = "";
    if (this.hasStatusFilterTarget) this.statusFilterTarget.value = "active";
    if (this.hasSearchInputTarget) this.searchInputTarget.value = "";
    this._updateSortHeaders("label", "ascending");
    this.refresh();
  }

  // ---------------------------------------------------------------------------
  // Activity filter dropdown (populated from GET /activities)
  // ---------------------------------------------------------------------------

  async _populateActivityFilter() {
    if (!this.hasActivityFilterTarget) return;
    try {
      const response = await api.listActivities();
      const activities = Array.isArray(response?.activities)
        ? response.activities
        : Array.isArray(response?.data) ? response.data : [];
      const activeOnly = activities.filter((a) => a.status === "active");

      for (const activity of activities) {
        this._activitiesCache.set(activity.id, activity);
      }

      for (const activity of activeOnly) {
        const option = document.createElement("option");
        option.value = activity.id;
        option.textContent = activity.description || "(sans libellé)";
        this.activityFilterTarget.appendChild(option);
      }
    } catch (error) {
      // Le filtre activité reste limité à « Toutes ». L'erreur est silencieuse
      // pour ne pas bloquer le chargement de la liste articles.
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    const seq = ++this._fetchSeq;
    if (this.hasTableWrapTarget) this.tableWrapTarget.setAttribute("aria-busy", "true");

    const params = { page: this.pageValue, perPage: this.perPageValue };
    if (this.qValue) params.q = this.qValue;
    if (this.sortValue) params.sort = this.sortValue;
    if (this.statusFilterValue) params["filter[status]"] = this.statusFilterValue;
    if (this.activityFilterValue) params["filter[activityId]"] = this.activityFilterValue;

    try {
      const response = await api.listArticles(params);
      if (seq !== this._fetchSeq) return;
      const articles = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination ?? { page: 1, perPage: this.perPageValue, total: 0, totalPages: 1 };
      this._renderRows(articles);
      this._renderSummary(pagination);
      this._renderPagination(pagination);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      this._renderError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger le catalogue.";
      this.showError(msg);
    } finally {
      if (seq === this._fetchSeq && this.hasTableWrapTarget) {
        this.tableWrapTarget.setAttribute("aria-busy", "false");
      }
    }
  }

  _renderRows(articles) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();

    if (articles.length === 0) {
      this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-article-empty"));
      return;
    }

    for (const article of articles) {
      const fragment = this.cloneTemplate("tpl-article-row");
      const detailUrl = `/articles-detail.html?id=${encodeURIComponent(article.id)}`;
      const isArchived = article.status === "archived";

      const activity = this._resolveActivity(article);
      const category = activity?.category ?? null;
      const categoryLabel = category ? (CATEGORY_LABELS[category] ?? category) : "—";

      this.fillSlots(fragment, {
        label: article.label ?? "—",
        reference: article.reference ?? "—",
        "category-label": categoryLabel,
        unit: article.unit ?? "—",
        price: this._formatPrice(article.priceExclVat),
        "status-label": isArchived ? "archivé" : "actif",
      });

      // Les deux <a> (libellé + bouton Voir) pointent vers la fiche détail.
      fragment.querySelectorAll('[data-slot="detail-href"]').forEach((el) => {
        el.setAttribute("href", detailUrl);
      });
      const actionLink = fragment.querySelector('[data-slot="action-href"]');
      if (actionLink) {
        actionLink.setAttribute("href", detailUrl);
        actionLink.setAttribute(
          "aria-label",
          `Voir l'article ${article.label ?? ""}`.trim(),
        );
      }

      // Coloration du badge catégorie selon BIC vente / BIC services / BNC.
      const categoryBadge = fragment.querySelector('[data-slot="category-badge"]');
      if (categoryBadge && category && CATEGORY_PILL_CLASSES[category]) {
        categoryBadge.classList.add(CATEGORY_PILL_CLASSES[category]);
      }

      // Pill statut : couleur olive pour actif, neutre (sans suffixe) pour archivé.
      const statusBadge = fragment.querySelector('[data-slot="status-badge"]');
      if (statusBadge && !isArchived) {
        statusBadge.classList.add("nm-pill--olive");
      }

      // Ligne grisée pour les articles archivés (style défini dans
      // notamicro.css : `.nm-table tbody tr.is-archived td`).
      if (isArchived) {
        const tr = fragment.querySelector("tr");
        if (tr) tr.classList.add("is-archived");
      }

      this.tableBodyTarget.appendChild(fragment);
    }
  }

  _renderSummary(pagination) {
    if (!this.hasSummaryTarget) return;
    const total = Number(pagination.total) || 0;
    const label = total === 0
      ? "Aucun article"
      : `${total} article${total > 1 ? "s" : ""}`;
    this.summaryTarget.textContent = label;
  }

  _renderPagination(pagination) {
    if (!this.hasPaginationTarget || !this.hasPageButtonsTarget || !this.hasPageInfoTarget) {
      return;
    }
    const page = Number(pagination.page) || 1;
    const perPage = Number(pagination.perPage) || this.perPageValue;
    const total = Number(pagination.total) || 0;
    const totalPages = Number(pagination.totalPages) || 1;

    if (total === 0 || totalPages <= 1) {
      this.paginationTarget.hidden = true;
      return;
    }
    this.paginationTarget.hidden = false;

    const start = (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, total);
    this.pageInfoTarget.textContent = `${start} — ${end} sur ${total} article${total > 1 ? "s" : ""}`;

    while (this.pageButtonsTarget.firstChild) {
      this.pageButtonsTarget.removeChild(this.pageButtonsTarget.firstChild);
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page - 1, text: "‹", label: "Page précédente", disabled: page <= 1,
    }));
    for (const token of this.getPagesToShow(page, totalPages)) {
      if (token === "...") {
        this.pageButtonsTarget.appendChild(this.cloneTemplate("tpl-pagination-ellipsis"));
        continue;
      }
      this.pageButtonsTarget.appendChild(this._buildPageButton({
        page: token,
        text: String(token),
        label: `Page ${token}`,
        current: token === page,
      }));
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page + 1, text: "›", label: "Page suivante", disabled: page >= totalPages,
    }));
  }

  _buildPageButton({ page, text, label, current = false, disabled = false }) {
    const fragment = this.cloneTemplate("tpl-pagination-page");
    const btn = fragment.querySelector("button");
    if (!btn) return fragment;
    btn.textContent = text;
    btn.setAttribute("aria-label", label);
    btn.dataset.page = String(page);
    if (disabled) btn.disabled = true;
    if (current) {
      btn.classList.add("is-current");
      btn.setAttribute("aria-current", "page");
    }
    return fragment;
  }

  _renderError() {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-article-error"));
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
    if (this.hasSummaryTarget) this.summaryTarget.textContent = "Erreur de chargement";
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  _resolveActivity(article) {
    // Two contract shapes tolerated jusqu'à figer 10.2 :
    //  (a) `article.activity` imbriqué : { id, description, category }
    //  (b) `article.activityId` seul, on rebobine via le cache rempli depuis
    //      `GET /activities` au connect().
    if (article.activity && typeof article.activity === "object") {
      return article.activity;
    }
    if (article.activityId && this._activitiesCache.has(article.activityId)) {
      return this._activitiesCache.get(article.activityId);
    }
    return null;
  }

  _formatPrice(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return PRICE_FORMATTER.format(n);
  }

  _updateSortHeaders(activeField, direction) {
    const buttons = this.element.querySelectorAll(".nm-th-sort[data-sort-field]");
    buttons.forEach((btn) => {
      if (btn.dataset.sortField === activeField) {
        btn.setAttribute("aria-sort", direction);
      } else {
        btn.setAttribute("aria-sort", "none");
      }
    });
  }
}
